import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertTriangle, Bell, CheckCircle2, Eye, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAlerts();
    
    // Listen for company changes to refresh data
    const handleCompanyChange = () => {
      setLoading(true);
      fetchAlerts();
    };
    
    window.addEventListener('companyChanged', handleCompanyChange);
    
    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange);
    };
  }, [filter]);

  const fetchAlerts = async () => {
    try {
      const url = filter === 'all' ? `${API_URL}/alerts` : `${API_URL}/alerts?status=${filter}`;
      const response = await axios.get(url);
      setAlerts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const updateAlertStatus = async (alertId, status) => {
    try {
      await axios.patch(`${API_URL}/alerts/${alertId}/status?status=${status}`);
      toast.success('Status atualizado!');
      fetchAlerts();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteAlert = async (alertId) => {
    if (!window.confirm('Deseja realmente deletar este alerta?')) return;
    
    try {
      await axios.delete(`${API_URL}/alerts/${alertId}`);
      toast.success('Alerta deletado!');
      fetchAlerts();
    } catch (error) {
      toast.error('Erro ao deletar alerta');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      baixa: 'bg-blue-500',
      media: 'bg-yellow-500',
      alta: 'bg-orange-500',
      critica: 'bg-red-500'
    };
    return colors[severity] || 'bg-gray-500';
  };

  const getSeverityIcon = (severity) => {
    const icons = {
      baixa: <Bell size={24} className="text-blue-500" />,
      media: <AlertTriangle size={24} className="text-yellow-500" />,
      alta: <AlertTriangle size={24} className="text-orange-500" />,
      critica: <AlertTriangle size={24} className="text-red-500" />
    };
    return icons[severity] || <Bell size={24} />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      novo: <Badge className="bg-red-500">Novo</Badge>,
      visualizado: <Badge className="bg-blue-500">Visualizado</Badge>,
      resolvido: <Badge className="bg-emerald-500">Resolvido</Badge>,
      ignorado: <Badge className="bg-gray-500">Ignorado</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    return `${diffDays}d atrás`;
  };

  if (loading) {
    return <div>Carregando...</div>;
  }

  const filteredAlerts = filter === 'all' 
    ? alerts 
    : alerts.filter(a => a.status === filter);

  const newAlerts = alerts.filter(a => a.status === 'novo');
  const visualizedAlerts = alerts.filter(a => a.status === 'visualizado');
  const resolvedAlerts = alerts.filter(a => a.status === 'resolvido');

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
          Sistema de Alertas Automáticos
        </h2>
        <p className="text-slate-600 dark:text-slate-400 mt-1">
          Alertas gerados automaticamente para riscos críticos
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Novos</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{newAlerts.length}</p>
            </div>
            <Bell size={24} className="text-red-500" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Visualizados</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{visualizedAlerts.length}</p>
            </div>
            <Eye size={24} className="text-blue-500" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Resolvidos</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{resolvedAlerts.length}</p>
            </div>
            <CheckCircle2 size={24} className="text-emerald-500" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-slate-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{alerts.length}</p>
            </div>
            <AlertTriangle size={24} className="text-slate-500" />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={filter} onValueChange={setFilter} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="all">Todos ({alerts.length})</TabsTrigger>
          <TabsTrigger value="novo">Novos ({newAlerts.length})</TabsTrigger>
          <TabsTrigger value="visualizado">Visualizados ({visualizedAlerts.length})</TabsTrigger>
          <TabsTrigger value="resolvido">Resolvidos ({resolvedAlerts.length})</TabsTrigger>
          <TabsTrigger value="ignorado">Ignorados</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => (
          <Card key={alert.id} className="p-6" data-testid={`alert-card-${alert.id}`}>
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${alert.severity === 'critica' ? 'bg-red-100 dark:bg-red-900' : alert.severity === 'alta' ? 'bg-orange-100 dark:bg-orange-900' : 'bg-yellow-100 dark:bg-yellow-900'}`}>
                {getSeverityIcon(alert.severity)}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900 dark:text-white">{alert.titulo}</h3>
                      {getStatusBadge(alert.status)}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{alert.descricao}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock size={14} />
                    {getTimeAgo(alert.created_at)}
                  </div>
                </div>

                {/* Ações Sugeridas */}
                {alert.acoes_sugeridas && alert.acoes_sugeridas.length > 0 && (
                  <div className="mt-4 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
                    <p className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-2">
                      ⚡ AÇÕES SUGERIDAS (geradas automaticamente):
                    </p>
                    <ul className="space-y-1">
                      {alert.acoes_sugeridas.map((acao, idx) => (
                        <li key={idx} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                          <span className="text-emerald-500 font-bold">•</span>
                          {acao}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                  {alert.status === 'novo' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateAlertStatus(alert.id, 'visualizado')}
                      data-testid={`mark-viewed-${alert.id}`}
                    >
                      <Eye size={16} className="mr-2" />
                      Marcar como Visualizado
                    </Button>
                  )}
                  {alert.status !== 'resolvido' && (
                    <Button
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600"
                      onClick={() => updateAlertStatus(alert.id, 'resolvido')}
                      data-testid={`mark-resolved-${alert.id}`}
                    >
                      <CheckCircle2 size={16} className="mr-2" />
                      Marcar como Resolvido
                    </Button>
                  )}
                  {alert.status !== 'ignorado' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updateAlertStatus(alert.id, 'ignorado')}
                      data-testid={`mark-ignored-${alert.id}`}
                    >
                      <XCircle size={16} className="mr-2" />
                      Ignorar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => deleteAlert(alert.id)}
                    data-testid={`delete-${alert.id}`}
                  >
                    Deletar
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredAlerts.length === 0 && (
        <Card className="p-12 text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
          <p className="text-slate-600 dark:text-slate-400">
            {filter === 'all' 
              ? 'Nenhum alerta no momento. O sistema está monitorando continuamente.'
              : `Nenhum alerta ${filter} no momento.`}
          </p>
        </Card>
      )}

      {/* Info Card */}
      <Card className="p-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">
          🔔 Sistema de Alertas Automáticos
        </h4>
        <p className="text-sm text-blue-800 dark:text-blue-200">
          Alertas são gerados automaticamente quando:
        </p>
        <ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 space-y-1">
          <li>• Um risco de nível ALTO ou GRAVE é identificado</li>
          <li>• Uma avaliação psicossocial indica risco crítico</li>
          <li>• Padrões de risco são detectados pelo sistema inteligente</li>
        </ul>
        <p className="text-sm text-blue-800 dark:text-blue-200 mt-3">
          O sistema sugere ações baseadas no tipo de risco para conformidade proativa e redução de incidentes.
        </p>
      </Card>
    </div>
  );
};

export default Alerts;
